// The early access page's pure helpers: filters → query, chart geometry and
// labels. No DOM.
import type { AdminSchemas } from "@10twenty/api/admin";

export type EarlyAccessPlatform = AdminSchemas["EarlyAccessPlatform"];
export type EarlyAccessStatus = "all" | "pending" | "confirmed";
export type EarlyAccessDay = AdminSchemas["EarlyAccessStats"]["days"][number];

export const PLATFORM_LABELS: Record<EarlyAccessPlatform, string> = {
  android: "Android",
  ios: "iPhone",
  both: "Both",
};

export const STATUS_LABELS: Record<EarlyAccessStatus, string> = {
  all: "All",
  pending: "Pending",
  confirmed: "Confirmed",
};

export interface EarlyAccessFilters {
  status: EarlyAccessStatus;
  platform: EarlyAccessPlatform | "";
  /** The search as typed; trimmed before it is sent. */
  q: string;
}

/** The most an address (and so a search) holds, in code points. */
export const MAX_SEARCH = 254;

/** `text` cut to `MAX_SEARCH` code points (an input's `maxLength` counts UTF-16 units). */
export function clipSearch(text: string): string {
  const points = [...text];
  return points.length > MAX_SEARCH ? points.slice(0, MAX_SEARCH).join("") : text;
}

/** Whether two filter sets ask for the same list (the search as it is sent). */
export function sameFilters(a: EarlyAccessFilters, b: EarlyAccessFilters): boolean {
  return a.status === b.status && a.platform === b.platform && a.q.trim() === b.q.trim();
}

/** Query parameters for `GET /early-access`; empty filters are left out. */
export function listQuery(filters: EarlyAccessFilters, cursor?: string) {
  const q = filters.q.trim();
  return {
    ...(filters.status !== "all" ? { status: filters.status } : {}),
    ...(filters.platform ? { platform: filters.platform } : {}),
    ...(q ? { q: clipSearch(q) } : {}),
    ...(cursor ? { cursor } : {}),
  };
}

/** "40 %" of a total, rounded; "—" when there is nothing to share. */
export function share(part: number, total: number): string {
  return total > 0 ? `${Math.round((part / total) * 100)} %` : "—";
}

export interface Bar {
  day: string;
  value: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Bars for one daily series in a `width` × `height` plot: one slot per day,
 * a 2 px gap between bars, heights against `max` (at least 1, so an empty
 * range draws flat). Zero days get no bar.
 */
export function dailyBars(
  days: readonly { day: string; value: number }[],
  width: number,
  height: number,
  max = Math.max(1, ...days.map((day) => day.value)),
): Bar[] {
  if (days.length === 0) return [];
  const slot = width / days.length;
  const barWidth = Math.max(1, slot - 2);
  return days.flatMap((day, index) => {
    if (day.value <= 0) return [];
    const barHeight = Math.max(1, (day.value / max) * height);
    return [
      { day: day.day, value: day.value, x: index * slot, y: height - barHeight, width: barWidth, height: barHeight },
    ];
  });
}

/** A round axis top at or above `max` (1, 2, 5 × 10ⁿ). */
export function niceMax(max: number): number {
  if (max <= 1) return 1;
  const power = 10 ** Math.floor(Math.log10(max));
  for (const step of [1, 2, 5, 10]) if (step * power >= max) return step * power;
  return 10 * power;
}

/** A UTC calendar day ("2026-10-17") as the operator's short date, e.g. "17 Oct". */
export function dayLabel(day: string, locale?: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString(locale, { day: "numeric", month: "short", timeZone: "UTC" });
}

/** The file name the server suggested in `Content-Disposition`, else a dated default. */
export function csvFileName(disposition: string | null, today = new Date()): string {
  const match = disposition?.match(/filename="([^"/\\]+)"/);
  return match?.[1] ?? `ten20-early-access-${today.toISOString().slice(0, 10)}.csv`;
}
